import { Avatar, Box, IconButton, Input, Typography } from "@mui/material";
import CustomAppBar from "../../../foundation/CustomAppBar/CustomAppBar";
import bg from "../../../assets/background.png";
import MoodIcon from "@mui/icons-material/Mood";
import MicIcon from "@mui/icons-material/Mic";
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RefreshIcon from "@mui/icons-material/Refresh";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import { useAuth } from 'react-oidc-context';

import ChatContainer from "./ChatContainer";
import ProfileSection from "./ProfileSection";
import AttachmentPopover from "./AttachmentPopover";
import { useState } from "react";

type Activity = {
  phone: string;
  type: string;
  message?: string;
  timestamp: string;
  admin_ref?: string;
  direction?: string;
};

type PlumberProfile = {
  phone: string;
  name?: string;
  wallet_balance?: number;
  total_earned?: number;
  address?: string;
  city?: string;
  state?: string;
  pin?: string;
  dob?: string;
  history?: Array<{
    date: string;
    code: string;
    type: "EARN" | "REDEMPTION";
    points: number;
  }>;
};

interface RightPanelProps {
  contact: null | {
    phone: string;
    lastText: string;
    lastSeen: string;
    messages: Activity[];
  };
  plumberProfile?: PlumberProfile | null;
  plumberError?: string | null;
  chatHistory?: Activity[];
  onRefresh?: () => void;
}

export default function RightPanel({ contact, plumberProfile, plumberError, chatHistory, onRefresh }: RightPanelProps) {
  const { user } = useAuth();
  const auth = useAuth();
  const globalIconStyle = {
    color: "#8696a1",
    height: "28px",
    width: "28px",
  };

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [textValue, setTextValue] = useState("");

  const handleLogout = () => {
    // Wipe all browser storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear local auth state
    auth.removeUser();
    
    // Redirect to Cognito logout endpoint
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_OIDC_CLIENT_ID;
    const logoutUri = window.location.origin;
    
    if (cognitoDomain && clientId) {
      const logoutUrl = `https://${cognitoDomain}.auth.${process.env.REACT_APP_COGNITO_REGION}.amazoncognito.com/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
      window.location.href = logoutUrl;
    }
  };

  return (
    <Box height="100%" width="70%" display="flex" flexDirection="column" sx={{ minHeight: 0 }}>
      <CustomAppBar>
        <Box
          width="100%"
          height="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display="flex">
            <Avatar />
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              pl="10px"
            >
              <Typography variant="body1" color="white">
                {contact?.phone || "No contact selected"}
              </Typography>
              <Typography variant="caption" color="#8496a0">
                {contact ? "online" : ""}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <IconButton
              onClick={onRefresh}
              aria-label="refresh"
              sx={{
                color: "#afbac0",
                '&:hover': {
                  color: '#00a884',
                  backgroundColor: 'transparent',
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
            <IconButton
              onClick={handleLogout}
              aria-label="logout"
              sx={{
                color: "#afbac0",
                '&:hover': {
                  color: '#d3396c',
                  backgroundColor: 'transparent',
                },
              }}
            >
              <PowerSettingsNewIcon />
            </IconButton>
          </Box>
        </Box>
      </CustomAppBar>
      <Box
        flex={1}
        minHeight={0}
        display="flex"
        flexDirection="row"
        sx={{
          background: "#101b20",
        }}
      >
        {/* Chat Section - 2/3 width */}
        <Box flex={2} position="relative" minWidth={0} sx={{ minHeight: 0 }}>
          <Box
            sx={{
              height: "100%",
              position: "relative",
              minHeight: 0,
              backgroundImage: `url(${bg})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Scrollable messages area */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              <ChatContainer messages={chatHistory && chatHistory.length > 0 ? chatHistory : (contact?.messages || [])} />
            </Box>

            {/* Fixed input footer inside chat column */}
            <Box sx={{ height: "72px", px: 2, py: 1, background: "transparent" }}>
              <Box
                height="100%"
                alignItems="center"
                display="flex"
                sx={{
                  background: "#1f2c33",
                  padding: "0px 12px",
                  borderRadius: "8px",
                  width: "100%",
                }}
              >
                <IconButton
                  onClick={() => {
                    setShowEmojiPicker(!showEmojiPicker);
                  }}
                >
                  <MoodIcon sx={globalIconStyle} />
                </IconButton>
                <AttachmentPopover />
                <Box flex={1} pl="5px" pr="5px">
                  <Input
                    fullWidth
                    disableUnderline
                    placeholder="Type a message"
                    value={textValue}
                    onChange={(event: any) => {
                      setTextValue(event.target.value);
                    }}
                    sx={{
                      background: "transparent",
                      height: "42px",
                      borderRadius: "6px",
                      color: "white",
                      padding: "0px 10px",
                    }}
                  />
                </Box>
                <IconButton onClick={() => {}}>
                  <MicIcon sx={globalIconStyle} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Profile Section - 1/3 width */}
        <ProfileSection plumber={plumberProfile || null} error={plumberError || null} />
      </Box>
      {/* Global bottom input removed — input is confined to chat column */}
      {showEmojiPicker && (
        <EmojiPicker
          height="45%"
          width="100%"
          previewConfig={{
            showPreview: false,
          }}
          onEmojiClick={(emojiData: EmojiClickData) => {
            setTextValue(textValue + emojiData.emoji);
          }}
        />
      )}
    </Box>
  );
}
